import { SessionContext } from '@benzinga/session-context';
import React from 'react';

import { GptSearchManager } from '@benzinga/benzinga-gpt-search-manager';
import {
  GptSearchItemData,
  GptSearchItemVersion,
  GptSearchModuleId,
  SearchItem,
  createGptSearchItem,
  isGptSearchItem,
} from '@benzinga/search-modules';
import {
  SearchModule,
  AutocompleteModule,
  ActionModule,
  TagModule,
  ClipboardModule,
  isNoCommaString,
  QueryScore,
} from './Modules';
import { SearchContext, SearchContextType } from '../context';
import { ResultHead, ResultDescription } from '../Expression/ExpressionSearch';
import styled from 'styled-components';
import { FaRobot } from 'react-icons/fa';
import { WidgetContext, WidgetId } from '@benzinga/widget-tools';
import Styles from '../styles/styles.module.scss';
export interface GptSearchModule extends SearchModule<GptSearchItemData> {
  id: typeof GptSearchModuleId;
  autocomplete: AutocompleteModule<GptSearchItemData>;
}

const createGptSearchClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isGptSearchItem(token)) return undefined;
      return [token];
    },
    onCopyString: token => {
      if (!isGptSearchItem(token)) return undefined;
      return isNoCommaString(token.data.prompt) ? [token.data.prompt] : undefined;
    },
    onPasteJSON: token => {
      if (!isGptSearchItem(token)) return undefined;
      return GptSearchModuleItemLoader(token);
    },
  };
};

const createGptSearchTagModule = (): TagModule => {
  return {
    render: item => {
      if (!isGptSearchItem(item)) return undefined;
      return {
        icon: <GptSearchIcon />,
        text: item.data.prompt,
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isGptSearchItem(item)) return undefined;
      return item.data.prompt;
    },
  };
};

const GptSearchModuleItemLoader = (item: SearchItem) => {
  switch (item.version) {
    case GptSearchItemVersion:
    default:
      return item as SearchItem<GptSearchItemData>;
  }
};
const createGptSearchActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

const PromptHistory: (prompt: string) => React.FC<{ isSelected: boolean; isLocked?: boolean }> =
  (prompt: string) => props => {
    return (
      <>
        <ResultHead $isSelected={props.isSelected} className={Styles.resultHead}>
          {'AI prompt'}
        </ResultHead>
        <ResultDescription $isSelected={props.isSelected} className={Styles.resultDescription}>
          {prompt}
        </ResultDescription>
      </>
    );
  };

const createGptSearchAutocompleteModule = (
  gptSearchManager: GptSearchManager,
  searchContext: SearchContextType,
  widgetId?: WidgetId,
): AutocompleteModule<GptSearchItemData> => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isGptSearchItem(item)) return undefined;
      return {
        component: PromptHistory(item.data.prompt ?? 'No AI prompt History Found'),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: GptSearchItemVersion,
      };
    },
    onSelect: item => {
      if (!isGptSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit = 10) => {
      const data = gptSearchManager.getCachedData(widgetId ?? '');
      const prompt = searchContext
        .getRecentModuleItem(widgetId)
        .filter(isGptSearchItem)
        .splice(0, 1)
        .map<[SearchItem<GptSearchItemData>, QueryScore]>(item => [item, QueryScore.Priority])[0];

      let results: [SearchItem<GptSearchItemData>, QueryScore][];

      if (text === '') {
        const uniqueMap = new Map<string, [SearchItem<GptSearchItemData>, QueryScore]>();
        const lastUsedPromptItems: [SearchItem<GptSearchItemData>, QueryScore][] = prompt ? [prompt] : [];
        [
          ...lastUsedPromptItems,
          ...data.map(item => [createGptSearchItem(item.prompt), QueryScore.StartsWithMatch] as const),
        ].forEach(([item, score]) => {
          if (!uniqueMap.has(item.data.prompt)) {
            uniqueMap.set(item.data.prompt, [item, score]);
          }
        });
        results = Array.from(uniqueMap.values());
      } else {
        const lower = text.toLowerCase();

        const previousPrompts = data
          .filter(d => d.prompt.toLowerCase().includes(lower))
          .map<[SearchItem<GptSearchItemData>, QueryScore]>(d => [createGptSearchItem(d.prompt), QueryScore.Priority]);
        const uniqueMap = new Map<string, [SearchItem<GptSearchItemData>, QueryScore]>();
        [[createGptSearchItem(text), QueryScore.Priority] as const, ...previousPrompts].forEach(([item, score]) => {
          if (!uniqueMap.has(item.data.prompt)) {
            uniqueMap.set(item.data.prompt, [item, score]);
          }
        });

        results = Array.from(uniqueMap.values());
      }

      return {
        cancel: () => [],
        promise: Promise.resolve({
          canceled: false,
          ok: results.slice(0, limit),
        }),
      };
    },
  };
};

export const createGptSearchModules = (
  gptSearchManager: GptSearchManager,
  search: SearchContextType,
  widgetId?: WidgetId,
): GptSearchModule => {
  return {
    action: createGptSearchActionModule(),
    autocomplete: createGptSearchAutocompleteModule(gptSearchManager, search, widgetId),
    clipboard: createGptSearchClipboardModule(),
    id: GptSearchModuleId,
    loader: GptSearchModuleItemLoader,
    state: 'beta',
    tag: createGptSearchTagModule(),
  };
};

export const useGptSearchModules = () => {
  const session = React.useContext(SessionContext);
  const search = React.useContext(SearchContext);
  const gptSearchManager = session.getManager(GptSearchManager);
  const widgetContext = React.useContext(WidgetContext);
  return React.useMemo(
    () => createGptSearchModules(gptSearchManager, search, widgetContext.widgetId),
    [gptSearchManager, search, widgetContext.widgetId],
  );
};

const GptSearchIcon = styled(FaRobot)`
  path {
    padding-top: 10px;
    height: 10px !important;
    width: 10px !important;
  }
`;
